import React, { useState, useEffect } from 'react';
import { Bell, CheckCheck, Archive, Clock, User } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  useNotifications,
  NotificationType,
  NotificationPriority,
} from '../contexts/NotificationContext';
import { useOrganization } from '../context/OrganizationContext';

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedOrg } = useOrganization();
  const {
    notifications,
    unreadCount,
    preferences,
    isLoading,
    isConnected,
    connectionStable,
    hasLoadedOnce,
    fetchNotifications,
    markAsRead,
    markAsClicked,
    archiveOldNotifications,
    updatePreferences,
  } = useNotifications();

  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [selectedNotifications, setSelectedNotifications] = useState<
    Set<string>
  >(new Set());

  // Fetch notifications on mount
  useEffect(() => {
    fetchNotifications();
  }, []);

  // Filter notifications
  const filteredNotifications = notifications.filter((notification) => {
    if (showUnreadOnly && notification.read_at) return false;
    if (selectedType !== 'all' && !notification.type.includes(selectedType))
      return false;
    if (
      selectedPriority !== 'all' &&
      notification.priority !== selectedPriority
    )
      return false;
    return true;
  });

  // Group notifications by date
  const groupedNotifications = filteredNotifications.reduce(
    (groups, notification) => {
      const date = new Date(notification.created_at);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);

      let groupLabel: string;
      if (date.toDateString() === today.toDateString()) {
        groupLabel = 'Today';
      } else if (date.toDateString() === yesterday.toDateString()) {
        groupLabel = 'Yesterday';
      } else if (date > weekAgo) {
        groupLabel = 'This Week';
      } else {
        groupLabel = 'Older';
      }

      if (!groups[groupLabel]) {
        groups[groupLabel] = [];
      }
      groups[groupLabel].push(notification);
      return groups;
    },
    {} as Record<string, typeof notifications>,
  );

  // Get notification icon based on type
  const getNotificationIcon = (type: NotificationType) => {
    if (type.includes('mention')) return '@';
    if (type.includes('assigned')) return '👤';
    if (type.includes('blocked')) return '🚫';
    if (type.includes('deadline')) return '⏰';
    if (type.includes('status')) return '📊';
    if (type.includes('comment')) return '💬';
    return '📢';
  };

  // Get priority color
  const getPriorityColor = (priority: NotificationPriority) => {
    switch (priority) {
      case NotificationPriority.HIGH:
        return 'text-red-600 dark:text-red-400';
      case NotificationPriority.MEDIUM:
        return 'text-yellow-600 dark:text-yellow-400';
      case NotificationPriority.LOW:
        return 'text-gray-600 dark:text-gray-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  // Handle notification click
  const handleNotificationClick = async (
    notification: (typeof notifications)[0],
  ) => {
    await markAsClicked(notification.id);

    // Navigate to the entity
    if (notification.entity_type && notification.entity_id) {
      const entityRoutes: Record<string, string> = {
        task: `/gravity-plans?taskId=${notification.entity_id}`,
        okr: `/okrs?okr=${notification.entity_id}`,
        company: `/companies/${notification.entity_id}`,
        proposition: `/propositions/${notification.entity_id}`,
        segment: `/segments/${notification.entity_id}`,
        position: `/positions/${notification.entity_id}`,
      };

      const route = entityRoutes[notification.entity_type];
      if (route) {
        navigate(route);
      }
    }
  };

  // Handle bulk actions
  const handleMarkSelectedAsRead = async () => {
    if (selectedNotifications.size > 0) {
      await markAsRead(Array.from(selectedNotifications));
      setSelectedNotifications(new Set());
    }
  };

  const toggleNotificationSelection = (id: string) => {
    const newSelection = new Set(selectedNotifications);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedNotifications(newSelection);
  };

  const selectAll = () => {
    if (selectedNotifications.size === filteredNotifications.length) {
      setSelectedNotifications(new Set());
    } else {
      setSelectedNotifications(new Set(filteredNotifications.map((n) => n.id)));
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Notifications
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {!hasLoadedOnce
                ? 'Loading notifications...'
                : unreadCount > 0
                  ? `You have ${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}`
                  : 'All caught up!'}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {/* Connection status temporarily hidden while fixing Cloud SQL issue
            {connectionStable && (
              <span
                className="flex items-center text-sm text-green-600 dark:text-green-400"
                title="New notifications will appear instantly"
              >
                <span className="h-2 w-2 bg-green-500 rounded-full animate-pulse mr-2" />
                Live updates
              </span>
            )}
            {isConnected && !connectionStable && (
              <span
                className="flex items-center text-sm text-yellow-600 dark:text-yellow-400"
                title="Establishing stable connection for real-time updates"
              >
                <span className="h-2 w-2 bg-yellow-500 rounded-full mr-2" />
                Connecting...
              </span>
            )}
            {!isConnected && hasLoadedOnce && (
              <span
                className="flex items-center text-sm text-gray-600 dark:text-gray-400"
                title="Refresh the page to check for new notifications"
              >
                <span className="h-2 w-2 bg-gray-500 rounded-full mr-2" />
                Updates paused
              </span>
            )}
            */}
            <button
              onClick={() => navigate(`/organization?tab=notifications`)}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
            >
              Settings
            </button>
          </div>
        </div>

        {/* Action Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          <div className="flex items-center space-x-3">
            {selectedNotifications.size > 0 && (
              <>
                <button
                  onClick={handleMarkSelectedAsRead}
                  className="px-3 py-1 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-700 dark:text-blue-300 rounded-md transition-colors"
                >
                  <CheckCheck className="h-4 w-4 inline mr-1" />
                  Mark as read ({selectedNotifications.size})
                </button>
                <button
                  onClick={() => setSelectedNotifications(new Set())}
                  className="px-3 py-1 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  Clear selection
                </button>
              </>
            )}
            {selectedNotifications.size === 0 && (
              <>
                <button
                  onClick={() => markAsRead(undefined, true)}
                  className="px-3 py-1 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-700 dark:text-blue-300 rounded-md transition-colors"
                >
                  <CheckCheck className="h-4 w-4 inline mr-1" />
                  Mark all as read
                </button>
                <button
                  onClick={() => archiveOldNotifications()}
                  className="px-3 py-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md transition-colors"
                >
                  <Archive className="h-4 w-4 inline mr-1" />
                  Archive old
                </button>
              </>
            )}
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-3">
            <label className="flex items-center text-sm">
              <input
                type="checkbox"
                checked={showUnreadOnly}
                onChange={(e) => setShowUnreadOnly(e.target.checked)}
                className="mr-2"
              />
              Unread only
            </label>

            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All types</option>
              <option value="mention">Mentions</option>
              <option value="assigned">Assignments</option>
              <option value="comment">Comments</option>
              <option value="status">Status changes</option>
              <option value="deadline">Deadlines</option>
            </select>

            <select
              value={selectedPriority}
              onChange={(e) => setSelectedPriority(e.target.value)}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All priorities</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="text-gray-500 dark:text-gray-400 mt-4">
              Loading notifications...
            </p>
          </div>
        ) : Object.keys(groupedNotifications).length === 0 ? (
          <div className="p-12 text-center">
            <Bell className="h-16 w-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No notifications found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {showUnreadOnly
                ? 'You have no unread notifications'
                : "You haven't received any notifications yet"}
            </p>
          </div>
        ) : (
          <>
            {/* Select all checkbox */}
            {filteredNotifications.length > 0 && (
              <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={
                      selectedNotifications.size ===
                      filteredNotifications.length
                    }
                    onChange={selectAll}
                    className="mr-2"
                  />
                  Select all
                </label>
              </div>
            )}

            {/* Grouped notifications */}
            {Object.entries(groupedNotifications).map(
              ([groupLabel, groupNotifications]) => (
                <div key={groupLabel}>
                  <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {groupLabel}
                  </div>
                  {groupNotifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`relative hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                        !notification.read_at
                          ? 'bg-blue-50/50 dark:bg-blue-900/20'
                          : ''
                      } ${selectedNotifications.has(notification.id) ? 'bg-blue-100/50 dark:bg-blue-800/30' : ''}`}
                    >
                      <div className="flex items-start p-4 border-b border-gray-200 dark:border-gray-700">
                        <input
                          type="checkbox"
                          checked={selectedNotifications.has(notification.id)}
                          onChange={() =>
                            toggleNotificationSelection(notification.id)
                          }
                          className="mt-1 mr-3"
                          onClick={(e) => e.stopPropagation()}
                        />

                        <div
                          className="flex-1 cursor-pointer"
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="flex items-start">
                            <span className="text-2xl mr-3">
                              {getNotificationIcon(notification.type)}
                            </span>

                            <div className="flex-1">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h3
                                    className={`font-medium ${getPriorityColor(notification.priority)}`}
                                  >
                                    {notification.title}
                                  </h3>
                                  <p className="text-gray-600 dark:text-gray-400 mt-1">
                                    {notification.message}
                                  </p>

                                  <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500 dark:text-gray-500">
                                    {notification.actor_name && (
                                      <span className="flex items-center">
                                        <User className="h-3 w-3 mr-1" />
                                        {notification.actor_name}
                                      </span>
                                    )}
                                    <span className="flex items-center">
                                      <Clock className="h-3 w-3 mr-1" />
                                      {formatDistanceToNow(
                                        new Date(notification.created_at),
                                        {
                                          addSuffix: true,
                                        },
                                      )}
                                    </span>
                                    {notification.group_count > 1 && (
                                      <span className="bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-2 py-0.5 rounded">
                                        +{notification.group_count - 1} similar
                                      </span>
                                    )}
                                  </div>
                                </div>

                                <div className="ml-4 flex-shrink-0">
                                  {!notification.read_at && (
                                    <span className="inline-block h-2 w-2 bg-blue-500 rounded-full" />
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ),
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
